import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Check,
  Circle,
  Shield,
  XCircle,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { UserService } from 'src/services/api/UserService';
import { getUser } from 'src/utils/authStorage';
import './ChangePassword.css';

/** Matches shared HTML: length, upper, lower, number, special (any non-alphanumeric). */
const getPasswordChecks = (value) => ({
  length: value.length >= 8,
  upper: /[A-Z]/.test(value),
  lower: /[a-z]/.test(value),
  number: /[0-9]/.test(value),
  special: /[^A-Za-z0-9]/.test(value),
});

const validatePassword = (value) =>
  Object.values(getPasswordChecks(value)).every(Boolean);

const getStrengthLevel = (score) => {
  if (score <= 1) return 'weak';
  if (score <= 2) return 'fair';
  if (score <= 3) return 'good';
  return 'strong';
};

const strengthLabel = {
  weak: 'Weak',
  fair: 'Fair',
  good: 'Good',
  strong: 'Strong',
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const userEmail = getUser()?.email;

  const [oldPassword, setOldPassword] = useState('');
  const [oldPasswordError, setOldPasswordError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  const checks = useMemo(() => getPasswordChecks(password), [password]);
  const score = useMemo(
    () => Object.values(checks).filter(Boolean).length,
    [checks]
  );
  const strengthLevel = useMemo(() => getStrengthLevel(score), [score]);

  const passwordsMatch =
    confirmPassword.length > 0 && password === confirmPassword;
  const passwordsMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;

  /** HTML: submit enabled when current filled, new length >= 8, confirm matches and non-empty. */
  const canSubmitHtml =
    oldPassword.trim().length > 0 &&
    password.length >= 8 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;

  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleOldPasswordChange = (e) => {
    const { value } = e.target;
    setOldPassword(value);
    setOldPasswordError('');
    setApiError('');
  };

  const handlePasswordChange = (e) => {
    const { value } = e.target;
    setPassword(value);
    setPasswordError('');
    setApiError('');
  };

  const handleConfirmPasswordChange = (e) => {
    const { value } = e.target;
    setConfirmPassword(value);
    setApiError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setOldPasswordError('');
    setPasswordError('');
    setApiError('');

    if (!oldPassword.trim()) {
      setOldPasswordError('Current password is required');
      return;
    }
    if (!password.trim()) {
      setPasswordError('New password is required');
      return;
    }
    if (!validatePassword(password)) {
      setPasswordError(
        'Use 8+ characters with uppercase, lowercase, number, and a special character.'
      );
      return;
    }
    if (!confirmPassword.trim()) {
      return;
    }
    if (password !== confirmPassword) {
      return;
    }
    if (oldPassword === password) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setIsLoading(true);
      await UserService.changePassword({
        current_password: oldPassword,
        new_password: password,
        confirm_password: confirmPassword,
      });
      setShowSuccess(true);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        'Failed to change password. Please try again.';
      setApiError(errorMessage);
      toast({
        variant: 'destructive',
        message: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const showStrengthBlock = Boolean(password);

  return (
    <div id="cp-change-password">
      <div className="app">
        {!showSuccess && apiError && (
          <div className="cp-api-error">{apiError}</div>
        )}

        <div className="pw-card">
          {!showSuccess ? (
            <>
              <div className="pw-card-header">
                <div className="pw-icon">
                  <Lock strokeWidth={2} />
                </div>
                <div className="pw-header-text">
                  <h2>Change password</h2>
                  <p>For your email sign-in</p>
                </div>
              </div>

              {userEmail && (
                <div className="pw-email-strip">
                  <div className="pw-email-badge">
                    <Mail strokeWidth={2} />
                    <span>{userEmail}</span>
                  </div>
                </div>
              )}

              <form
                id="change-password-form"
                onSubmit={handleSubmit}
                className="pw-form"
                noValidate
              >
                <div className="fg">
                  <label className="fl" htmlFor="cp-current-pw">
                    Current Password
                  </label>
                  <div className="fi-wrap">
                    <input
                      id="cp-current-pw"
                      className={`fi${oldPasswordError ? ' error' : ''}`}
                      type={showOldPassword ? 'text' : 'password'}
                      placeholder="Enter current password"
                      autoComplete="current-password"
                      value={oldPassword}
                      onChange={handleOldPasswordChange}
                    />
                    <button
                      type="button"
                      className="toggle-pw"
                      aria-label="Toggle password visibility"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                    >
                      {showOldPassword ? (
                        <EyeOff strokeWidth={2} />
                      ) : (
                        <Eye strokeWidth={2} />
                      )}
                    </button>
                  </div>
                  {oldPasswordError && (
                    <div className="fhint error">{oldPasswordError}</div>
                  )}
                </div>

                <div className="fg">
                  <label className="fl" htmlFor="cp-new-pw">
                    New Password
                  </label>
                  <div className="fi-wrap">
                    <input
                      id="cp-new-pw"
                      className="fi"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      value={password}
                      onChange={handlePasswordChange}
                    />
                    <button
                      type="button"
                      className="toggle-pw"
                      aria-label="Toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff strokeWidth={2} />
                      ) : (
                        <Eye strokeWidth={2} />
                      )}
                    </button>
                  </div>

                  {showStrengthBlock && (
                    <>
                      <div className="pw-strength">
                        <div className="pw-strength-bar-track">
                          <div
                            className={`pw-strength-bar-fill ${strengthLevel}`}
                          />
                        </div>
                        <div className={`pw-strength-label ${strengthLevel}`}>
                          {strengthLabel[strengthLevel]}
                        </div>
                      </div>

                      <div className="pw-requirements">
                        <div className={`pw-req${checks.length ? ' met' : ''}`}>
                          {checks.length ? (
                            <Check strokeWidth={2.5} />
                          ) : (
                            <Circle strokeWidth={2.5} />
                          )}
                          <span>8+ characters</span>
                        </div>
                        <div className={`pw-req${checks.upper ? ' met' : ''}`}>
                          {checks.upper ? (
                            <Check strokeWidth={2.5} />
                          ) : (
                            <Circle strokeWidth={2.5} />
                          )}
                          <span>Uppercase letter</span>
                        </div>
                        <div className={`pw-req${checks.lower ? ' met' : ''}`}>
                          {checks.lower ? (
                            <Check strokeWidth={2.5} />
                          ) : (
                            <Circle strokeWidth={2.5} />
                          )}
                          <span>Lowercase letter</span>
                        </div>
                        <div className={`pw-req${checks.number ? ' met' : ''}`}>
                          {checks.number ? (
                            <Check strokeWidth={2.5} />
                          ) : (
                            <Circle strokeWidth={2.5} />
                          )}
                          <span>Number</span>
                        </div>
                        <div
                          className={`pw-req${checks.special ? ' met' : ''}`}
                        >
                          {checks.special ? (
                            <Check strokeWidth={2.5} />
                          ) : (
                            <Circle strokeWidth={2.5} />
                          )}
                          <span>Special character</span>
                        </div>
                      </div>
                    </>
                  )}

                  {passwordError && (
                    <div className="fhint error">{passwordError}</div>
                  )}
                </div>

                <div className="fg">
                  <label className="fl" htmlFor="cp-confirm-pw">
                    Confirm New Password
                  </label>
                  <div className="fi-wrap">
                    <input
                      id="cp-confirm-pw"
                      className={`fi${
                        passwordsMismatch ? ' error' : ''
                      }${passwordsMatch ? ' valid' : ''}`}
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                    />
                    <button
                      type="button"
                      className="toggle-pw"
                      aria-label="Toggle password visibility"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff strokeWidth={2} />
                      ) : (
                        <Eye strokeWidth={2} />
                      )}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && (
                    <div
                      className={`fhint${passwordsMatch ? ' success' : ' error'}`}
                    >
                      {passwordsMatch ? (
                        <>
                          <Check strokeWidth={2.5} /> Passwords match
                        </>
                      ) : (
                        <>
                          <XCircle strokeWidth={2.5} /> Passwords do not match
                        </>
                      )}
                    </div>
                  )}
                </div>
              </form>

              <div className="pw-actions">
                <button
                  type="submit"
                  form="change-password-form"
                  className="btn-primary"
                  disabled={!canSubmitHtml || isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="spinner" />
                      Updating…
                    </>
                  ) : (
                    <>
                      <Lock strokeWidth={2.5} />
                      Update Password
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="pw-success show">
              <div className="pw-success-icon">
                <Check strokeWidth={2.5} />
              </div>
              <h3>Password Updated</h3>
              <p>Your password has been changed successfully.</p>
            </div>
          )}
        </div>

        {!showSuccess && (
          <div className="security-note">
            <Shield strokeWidth={2} />
            <span>
              Your password is encrypted and never stored in plain text.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangePassword;
